/**
 * Hook para gerenciar estado global do Flow Builder
 * Inclui operações de CRUD no Supabase, gerenciamento de variáveis e validação
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FlowNode, FlowEdge, FlowVariable, FlowStatus } from '@/types/flows';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

interface UseFlowBuilderProps {
  flowId?: string;
}

/**
 * Hook principal do Flow Builder
 * Gerencia todo o estado e operações do flow
 */
export function useFlowBuilder({ flowId }: UseFlowBuilderProps = {}) {
  const { currentTenant, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Estado do flow
  // Gera nome único com data/hora para evitar colisão com o unique index do banco (unique_flow_name_per_tenant)
  const defaultFlowName = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `Novo Fluxo - ${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };
  const [flowName, setFlowName] = useState(defaultFlowName);
  const [flowDescription, setFlowDescription] = useState('');
  const [flowStatus, setFlowStatus] = useState<FlowStatus>('draft');
  const [nodes, setNodes] = useState<FlowNode[]>([
    {
      id: 'start-1',
      type: 'start',
      position: { x: 250, y: 100 },
      data: {
        type: 'start',
        label: 'Início',
        config: {},
      },
    },
  ]);
  const [edges, setEdges] = useState<FlowEdge[]>([]);
  const [variables, setVariables] = useState<FlowVariable[]>([]);
  const [triggerConfig, setTriggerConfig] = useState<any[]>([]); // Configuração de gatilhos do flow
  
  // Estado de controle
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  /**
   * IDs de nodes com problemas de validação detectados na publicação.
   * Usado para destacá-los visualmente no canvas (borda vermelha pulsante)
   * e listar no banner/toast para que o usuário identifique rapidamente
   * onde estão os erros.
   */
  const [invalidNodeIds, setInvalidNodeIds] = useState<string[]>([]);

  /**
   * Carrega flow existente do Supabase
   */
  const loadFlow = useCallback(async (id: string) => {
    if (!currentTenant) return;

    setIsLoading(true);
    logger.info('useFlowBuilder', 'Carregando flow', { flowId: id });

    try {
      const { data, error } = await supabase
        .from('flows')
        .select('*')
        .eq('id', id)
        .eq('tenant_id', currentTenant.id)
        .single();

      if (error) throw error;

      if (data) {
        setFlowName(data.name);
        setFlowDescription(data.description || '');
        setFlowStatus(data.status);
        setNodes((data.nodes as any) || []);
        setEdges((data.edges as any) || []);
        setVariables((data.variables as any) || []);
        // Carrega configuração de gatilhos
        setTriggerConfig(Array.isArray(data.trigger_config) ? data.trigger_config : []);
        setHasChanges(false);
        logger.info('useFlowBuilder', 'Flow carregado com sucesso', { flowId: id });
        toast.success('Flow carregado com sucesso');
      }
    } catch (error) {
      logger.error('useFlowBuilder', 'Erro ao carregar flow', { error });
      toast.error('Erro ao carregar flow');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant]);

  /**
   * Invalida o cache de triggers do tenant
   * Chamado após salvar/publicar/pausar/arquivar flows
   */
  const invalidateTriggerCache = useCallback(async () => {
    if (!currentTenant?.id) return;
    
    logger.debug('useFlowBuilder', 'Invalidando cache de triggers', { tenantId: currentTenant.id });
    
    const { error } = await supabase
      .from('trigger_cache')
      .delete()
      .eq('tenant_id', currentTenant.id);
    
    if (error) {
      logger.error('useFlowBuilder', 'Erro ao invalidar cache', { error });
    } else {
      logger.debug('useFlowBuilder', 'Cache de triggers invalidado');
    }
  }, [currentTenant?.id]);

  /**
   * Cancela execuções que estão aguardando delay para este flow
   * Chamado quando o flow é editado, pausado ou arquivado
   * Isso garante que execuções com configuração antiga não sejam retomadas
   */
  const cancelPendingDelays = useCallback(async (flowIdToCancel: string) => {
    if (!flowIdToCancel || flowIdToCancel === 'new') return;
    
    logger.debug('useFlowBuilder', 'Cancelando execuções waiting_delay', { flowId: flowIdToCancel });
    
    const { error } = await supabase
      .from('flow_executions')
      .update({ 
        status: 'cancelled',
        completed_at: new Date().toISOString(),
        scheduled_resume_at: null,
      })
      .eq('flow_id', flowIdToCancel)
      .eq('status', 'waiting_delay');
    
    if (error) {
      logger.error('useFlowBuilder', 'Erro ao cancelar execuções pendentes', { error });
    } else {
      logger.info('useFlowBuilder', 'Execuções waiting_delay canceladas', { flowId: flowIdToCancel });
    }
  }, []);

  /**
   * Salva flow no Supabase (criar ou atualizar)
   */
  const saveFlow = useCallback(async () => {
    if (!currentTenant || !user) {
      toast.error('Você precisa estar autenticado');
      return null;
    }

    // Validação básica
    if (!flowName.trim()) {
      toast.error('O nome do flow é obrigatório');
      return null;
    }

    if (nodes.length === 0) {
      toast.error('Adicione pelo menos um node ao flow');
      return null;
    }

    // Garante que existe um node de início
    const hasStartNode = nodes.some(n => n.data.type === 'start');
    if (!hasStartNode) {
      toast.error('O flow precisa ter um node de Início');
      return null;
    }

    setIsSaving(true);
    logger.info('useFlowBuilder', 'Salvando flow', { flowId, flowName });

    try {
      const flowData = {
        tenant_id: currentTenant.id,
        name: flowName.trim(),
        description: flowDescription.trim() || null,
        status: flowStatus,
        nodes: JSON.parse(JSON.stringify(nodes)),
        edges: JSON.parse(JSON.stringify(edges)),
        variables: JSON.parse(JSON.stringify(variables)),
        settings: {},
        created_by: user.id,
        // NÃO incluir trigger_config aqui - deixar o valor existente no banco
        // trigger_config é gerenciado separadamente via ConfigureTriggersDialog
      };

      if (flowId && flowId !== 'new') {
        // Atualizar flow existente
        const { data, error } = await supabase
          .from('flows')
          .update({
            ...flowData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', flowId)
          .eq('tenant_id', currentTenant.id)
          .select()
          .single();

        if (error) throw error;

        logger.info('useFlowBuilder', 'Flow atualizado com sucesso', { flowId });
        toast.success('Flow salvo com sucesso');
        setHasChanges(false);
        setLastSavedAt(new Date());
        
        // Invalida cache da listagem de fluxos (atualiza contadores, status, nome, etc)
        await queryClient.invalidateQueries({ queryKey: ['flows'] });
        
        // Cancela execuções pendentes com waiting_delay (configuração pode ter mudado)
        await cancelPendingDelays(flowId);
        
        // Invalida cache de triggers do tenant (triggers podem ter mudado)
        await invalidateTriggerCache();
        
        return data.id;
      } else {
        // Criar novo flow
        const { data, error } = await supabase
          .from('flows')
          .insert(flowData)
          .select()
          .single();

        if (error) throw error;

        logger.info('useFlowBuilder', 'Flow criado com sucesso', { newFlowId: data.id });
        toast.success('Flow criado com sucesso');
        setHasChanges(false);
        setLastSavedAt(new Date());
        
        // Invalida cache da listagem de fluxos (novo flow deve aparecer imediatamente)
        await queryClient.invalidateQueries({ queryKey: ['flows'] });
        
        // Invalida cache de triggers do tenant (novo flow pode ter triggers)
        await invalidateTriggerCache();
        
        // Navega para a URL com o ID do flow recém-criado
        navigate(`/tenant/flows/${data.id}`, { replace: true });
        return data.id;
      }
    } catch (error) {
      logger.error('useFlowBuilder', 'Erro ao salvar flow', { error });
      toast.error('Erro ao salvar flow');
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [currentTenant, user, flowId, flowName, flowDescription, flowStatus, nodes, edges, variables, navigate, invalidateTriggerCache]);

  /**
   * Salva configuração de gatilhos do flow
   * Função separada pois trigger_config é gerenciado independentemente via ConfigureTriggersDialog
   */
  const saveTriggerConfig = useCallback(async (triggers: any[]) => {
    if (!flowId || flowId === 'new') {
      toast.error('Salve o flow primeiro antes de configurar gatilhos');
      return false;
    }

    if (!currentTenant?.id) {
      toast.error('Tenant não encontrado');
      return false;
    }

    logger.info('useFlowBuilder', 'Salvando configuração de gatilhos', { flowId, triggers });

    try {
      // Passo 1: Salvar trigger_config no banco
      const { error } = await supabase
        .from('flows')
        .update({ trigger_config: triggers })
        .eq('id', flowId)
        .eq('tenant_id', currentTenant.id);

      if (error) throw error;

      // Passo 2: Gerar token automaticamente se há gatilho webhook e token não existe
      const hasWebhookTrigger = triggers.some((t: any) => t.type === 'webhook');
      if (hasWebhookTrigger) {
        // Verificar se já tem token antes de regenerar
        const { data: flowData } = await supabase
          .from('flows')
          .select('webhook_token')
          .eq('id', flowId)
          .single();
        
        if (!flowData?.webhook_token) {
          logger.info('useFlowBuilder', 'Detectado gatilho webhook sem token, gerando automaticamente...', { flowId });
          const { data: newToken, error: tokenError } = await supabase.rpc('regenerate_flow_webhook_token', {
            p_flow_id: flowId
          });
          
          if (tokenError) {
            logger.warn('useFlowBuilder', 'Erro ao gerar token de webhook', { error: tokenError });
            toast.warning('Gatilhos salvos, mas erro ao gerar token do webhook. Regenere manualmente.');
          } else {
            logger.info('useFlowBuilder', 'Token de webhook gerado com sucesso', { token: newToken?.substring(0, 10) });
          }
        }
      }

      // Passo 3: Atualizar estado local e invalidar cache
      setTriggerConfig(triggers);
      await invalidateTriggerCache();
      // Passo 3.1: Invalidar cache da listagem de flows para refletir trigger_config atualizado
      await queryClient.invalidateQueries({ queryKey: ['flows'] });
      
      logger.info('useFlowBuilder', 'Gatilhos salvos com sucesso', { flowId });
      toast.success('Gatilhos salvos com sucesso');
      return true;
    } catch (error) {
      logger.error('useFlowBuilder', 'Erro ao salvar gatilhos', { error });
      toast.error('Erro ao salvar gatilhos');
      return false;
    }
  }, [flowId, currentTenant?.id, invalidateTriggerCache, queryClient]);

  /**
   * Helper interno: retorna o label "amigável" do node para exibir nas mensagens de erro.
   * Faz fallback para o tipo + ID curto caso não haja label configurado.
   */
  const getNodeDisplayName = useCallback((node: FlowNode): string => {
    const label = (node.data?.label || '').toString().trim();
    if (label) return label;
    const type = node.data?.type || node.type || 'node';
    const shortId = node.id.slice(0, 6).toUpperCase();
    return `${type} (${shortId})`;
  }, []);

  /**
   * Publica o flow (ativa).
   *
   * Estratégia de validação:
   * 1) Coleta TODOS os nodes com problemas de conexão (não para no primeiro).
   * 2) Atualiza `invalidNodeIds` para que o canvas destaque visualmente.
   * 3) Mostra toast com os NOMES dos nodes problemáticos + ação "Localizar".
   * 4) Em caso de problema configurável (variantes A/B, condition sem comparações,
   *    etc.) ainda usa toasts específicos pois são erros de configuração interna.
   */
  const publishFlow = useCallback(async () => {
    // Acumula IDs problemáticos para destacar no canvas
    const problematicIds = new Set<string>();
    // Acumula mensagens descritivas para o toast principal
    const problemMessages: string[] = [];

    // ---------- 0. Validação de gatilhos: precisa ter pelo menos 1 configurado ----------
    // Espelha a trava do trigger DB `trg_validate_flow_active_requires_trigger`.
    // Evita publicar fluxo sem gatilho (causa raiz que derrubava o trigger_cache do webhook).
    if (!Array.isArray(triggerConfig) || triggerConfig.length === 0) {
      toast.error('Configure ao menos 1 gatilho antes de ativar o fluxo', {
        description: 'Abra as configurações do fluxo (engrenagem) e adicione um gatilho.',
        duration: 8000,
      });
      return false;
    }

    // ---------- 1. Validação básica: precisa ter início ----------
    const startNodes = nodes.filter(n => n.data.type === 'start');
    if (startNodes.length === 0) {
      toast.error('O flow precisa ter um node de início');
      setInvalidNodeIds([]);
      return false;
    }


    const endNodes = nodes.filter(n => n.data.type === 'end');
    if (endNodes.length === 0) {
      toast.warning('É recomendado ter pelo menos um node de fim');
    }

    // ---------- 2. Nodes totalmente desconectados (sem nenhuma edge) ----------
    const connectedNodeIds = new Set<string>();
    edges.forEach(edge => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });

    // GroupNodes são containers visuais sem handles, por design nunca participam de edges.
    // Por isso são excluídos da validação de "desconectado" (start/end também são exceções legítimas).
    const disconnectedNodes = nodes.filter(
      n => n.data.type !== 'start'
        && n.data.type !== 'end'
        && n.data.type !== 'group'
        && !connectedNodeIds.has(n.id)
    );

    disconnectedNodes.forEach(n => {
      problematicIds.add(n.id);
      problemMessages.push(`"${getNodeDisplayName(n)}" desconectado`);
    });

    // ---------- 3. QuestionNodes com opções sem conexão ----------
    const questionNodes = nodes.filter(
      n => n.data.type === 'question_buttons' || n.data.type === 'question_list'
    );

    for (const questionNode of questionNodes) {
      const options = questionNode.data.config?.options || [];

      if (options.length === 0) {
        problematicIds.add(questionNode.id);
        problemMessages.push(`"${getNodeDisplayName(questionNode)}" sem opções configuradas`);
        continue;
      }

      const missingConnections: string[] = [];
      for (let i = 0; i < options.length; i++) {
        const handleId = `option_${i}`;
        const hasConnection = edges.some(
          edge => edge.source === questionNode.id && edge.sourceHandle === handleId
        );
        if (!hasConnection) {
          const optionLabel = options[i].label || options[i].title || `Opção ${i + 1}`;
          missingConnections.push(optionLabel);
        }
      }

      if (missingConnections.length > 0) {
        problematicIds.add(questionNode.id);
        problemMessages.push(
          `"${getNodeDisplayName(questionNode)}" com opção(ões) sem conexão: ${missingConnections.slice(0, 3).join(', ')}${missingConnections.length > 3 ? '...' : ''}`
        );
      }
    }

    // ---------- 4. GotoNodes com destino inválido ----------
    const gotoNodes = nodes.filter(n => n.data.type === 'goto');
    for (const gotoNode of gotoNodes) {
      const targetNodeId = gotoNode.data.config?.target_node_id;

      if (!targetNodeId) {
        problematicIds.add(gotoNode.id);
        problemMessages.push(`"${getNodeDisplayName(gotoNode)}" (Pular) sem destino configurado`);
        continue;
      }

      const targetExists = nodes.some(n => n.id === targetNodeId);
      if (!targetExists) {
        problematicIds.add(gotoNode.id);
        problemMessages.push(`"${getNodeDisplayName(gotoNode)}" (Pular) com destino inexistente`);
      }
    }

    // ---------- 5. ConditionNodes com IF/Else sem conexão ----------
    const conditionNodes = nodes.filter(n => n.data.type === 'condition');
    for (const conditionNode of conditionNodes) {
      const config = conditionNode.data.config || {};
      const comparisons = config.comparisons || [];

      if (comparisons.length > 0) {
        const missingConnections: string[] = [];

        for (let i = 0; i < comparisons.length; i++) {
          const handleId = `if_${i}`;
          const hasConnection = edges.some(
            edge => edge.source === conditionNode.id && edge.sourceHandle === handleId
          );
          if (!hasConnection) missingConnections.push(`IF #${i + 1}`);
        }

        const hasElseConnection = edges.some(
          edge => edge.source === conditionNode.id && edge.sourceHandle === 'else'
        );
        if (!hasElseConnection) missingConnections.push('Else');

        if (missingConnections.length > 0) {
          problematicIds.add(conditionNode.id);
          problemMessages.push(
            `Condição "${getNodeDisplayName(conditionNode)}" sem conexão em: ${missingConnections.join(', ')}`
          );
        }
      } else {
        // Formato legado true/false
        const hasTrueConnection = edges.some(
          edge => edge.source === conditionNode.id && edge.sourceHandle === 'true'
        );
        const hasFalseConnection = edges.some(
          edge => edge.source === conditionNode.id && edge.sourceHandle === 'false'
        );
        if (!hasTrueConnection || !hasFalseConnection) {
          problematicIds.add(conditionNode.id);
          problemMessages.push(
            `Condição "${getNodeDisplayName(conditionNode)}" precisa ter conexões para Verdadeiro e Falso`
          );
        }
      }
    }

    // ---------- 6. ABTestNodes ----------
    const abTestNodes = nodes.filter(n => n.data.type === 'ab_test');
    for (const abNode of abTestNodes) {
      const config = abNode.data.config || {};
      const variants = config.variants || [];

      if (variants.length < 2) {
        problematicIds.add(abNode.id);
        problemMessages.push(`Teste A/B "${getNodeDisplayName(abNode)}" precisa de pelo menos 2 variantes`);
        continue;
      }
      if (variants.length > 4) {
        problematicIds.add(abNode.id);
        problemMessages.push(`Teste A/B "${getNodeDisplayName(abNode)}" pode ter no máximo 4 variantes`);
        continue;
      }

      const missingConnections: string[] = [];
      for (let i = 0; i < variants.length; i++) {
        const hasConnection = edges.some(e =>
          e.source === abNode.id && e.sourceHandle === `variant_${i}`
        );
        if (!hasConnection) missingConnections.push(variants[i].label);
      }
      if (missingConnections.length > 0) {
        problematicIds.add(abNode.id);
        problemMessages.push(
          `Teste A/B "${getNodeDisplayName(abNode)}" sem conexão em: ${missingConnections.join(', ')}`
        );
      }

      const total = variants.reduce((sum: number, v: any) => sum + (v.percentage || 0), 0);
      if (Math.abs(total - 100) > 0.01) {
        problematicIds.add(abNode.id);
        problemMessages.push(
          `Teste A/B "${getNodeDisplayName(abNode)}" com porcentagens em ${total}% (deve somar 100%)`
        );
      }
    }

    // ---------- 7. Se houver QUALQUER problema, bloqueia publicação ----------
    if (problematicIds.size > 0) {
      const idsArray = Array.from(problematicIds);
      setInvalidNodeIds(idsArray);

      logger.warn('useFlowBuilder', '[publishFlow] Validação falhou', {
        count: idsArray.length,
        invalidNodeIds: idsArray,
        problems: problemMessages,
      });

      // Monta mensagem resumida (até 3 problemas para não estourar)
      const previewMessages = problemMessages.slice(0, 3).join(' • ');
      const extra = problemMessages.length > 3 ? ` (+${problemMessages.length - 3} outros)` : '';

      toast.error(
        `${idsArray.length} node(s) com problema. ${previewMessages}${extra}`,
        {
          duration: 10000,
          description: 'Os nodes problemáticos estão destacados em vermelho no canvas.',
        }
      );
      return false;
    }

    // ---------- 8. Tudo OK: limpa destaques e publica ----------
    setInvalidNodeIds([]);

    setFlowStatus('active');
    setHasChanges(true);

    const savedId = await saveFlow();
    if (savedId) {
      // Atualiza published_at
      await supabase
        .from('flows')
        .update({ published_at: new Date().toISOString() })
        .eq('id', savedId);

      // Invalida cache de triggers do tenant
      await invalidateTriggerCache();

      toast.success('Flow publicado e ativo!');
      return true;
    }

    return false;
  }, [nodes, edges, saveFlow, invalidateTriggerCache, getNodeDisplayName, triggerConfig]);

  /**
   * Pausa o flow
   */
  const pauseFlow = useCallback(async () => {
    setFlowStatus('paused');
    setHasChanges(true);
    await saveFlow();
    // Cancela execuções pendentes do flow pausado
    if (flowId && flowId !== 'new') {
      await cancelPendingDelays(flowId);
    }
    // Invalida cache de triggers do tenant
    await invalidateTriggerCache();
    toast.info('Flow pausado');
  }, [saveFlow, invalidateTriggerCache, flowId, cancelPendingDelays]);

  /**
   * Arquiva o flow
   */
  const archiveFlow = useCallback(async () => {
    setFlowStatus('archived');
    setHasChanges(true);
    await saveFlow();
    // Cancela execuções pendentes do flow arquivado
    if (flowId && flowId !== 'new') {
      await cancelPendingDelays(flowId);
    }
    // Invalida cache de triggers do tenant
    await invalidateTriggerCache();
    toast.info('Flow arquivado');
  }, [saveFlow, invalidateTriggerCache, flowId, cancelPendingDelays]);

  /**
   * Adiciona variável ao flow
   */
  const addVariable = useCallback((variable: FlowVariable) => {
    // Valida se o nome da variável já existe
    const exists = variables.some(v => v.name === variable.name);
    if (exists) {
      toast.error(`Variável "${variable.name}" já existe`);
      return false;
    }

    setVariables(prev => [...prev, variable]);
    setHasChanges(true);
    toast.success('Variável adicionada');
    return true;
  }, [variables]);

  /**
   * Remove variável do flow
   */
  const removeVariable = useCallback((variableName: string) => {
    setVariables(prev => prev.filter(v => v.name !== variableName));
    setHasChanges(true);
    toast.success('Variável removida');
  }, []);

  /**
   * Atualiza variável existente
   */
  const updateVariable = useCallback((oldName: string, newVariable: FlowVariable) => {
    setVariables(prev => prev.map(v => v.name === oldName ? newVariable : v));
    setHasChanges(true);
    toast.success('Variável atualizada');
  }, []);

  /**
   * Atualiza nodes e marca como alterado
   * Garante que o node de Início sempre existe
   */
  const updateNodes = useCallback((newNodes: FlowNode[]) => {
    // Verifica se existe node de início
    const hasStartNode = newNodes.some(n => n.data.type === 'start');
    
    if (!hasStartNode) {
      toast.error('O node de Início é obrigatório e não pode ser removido');
      return;
    }

    setNodes(newNodes);
    setHasChanges(true);
  }, []);

  /**
   * Atualiza edges e marca como alterado
   */
  const updateEdges = useCallback((newEdges: FlowEdge[]) => {
    setEdges(newEdges);
    setHasChanges(true);
  }, []);

  /**
   * Atualiza configuração de um node específico
   */
  const updateNodeConfig = useCallback((nodeId: string, config: any) => {
    setNodes(prev => prev.map(node => 
      node.id === nodeId 
        ? { ...node, data: { ...node.data, config } }
        : node
    ));
    setHasChanges(true);
  }, []);

  /**
   * Atualiza nome do flow
   */
  const updateFlowName = useCallback((name: string) => {
    setFlowName(name);
    setHasChanges(true);
  }, []);

  /**
   * Atualiza descrição do flow
   */
  const updateFlowDescription = useCallback((description: string) => {
    setFlowDescription(description);
    setHasChanges(true);
  }, []);

  /**
   * Carrega flow ao montar se flowId for fornecido e não for 'new'
   */
  useEffect(() => {
    // Ignora se flowId for 'new' - significa criação de novo fluxo
    if (flowId && flowId !== 'new' && currentTenant) {
      loadFlow(flowId);
    }
  }, [flowId, currentTenant, loadFlow]);

  /**
   * Alerta ao sair com mudanças não salvas
   */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  /**
   * Auto-limpeza de invalidNodeIds:
   * Sempre que o usuário modifica nodes/edges, re-verifica se cada node
   * marcado como inválido continua desconectado. Remove do destaque os que
   * já foram corrigidos. Isso dá feedback imediato sem precisar republicar.
   */
  useEffect(() => {
    if (invalidNodeIds.length === 0) return;

    const connectedIds = new Set<string>();
    edges.forEach(e => {
      connectedIds.add(e.source);
      connectedIds.add(e.target);
    });

    const stillInvalid = invalidNodeIds.filter(id => {
      const node = nodes.find(n => n.id === id);
      if (!node) return false; // node removido => limpa
      // Mantém apenas se continuar totalmente desconectado
      // (validações específicas por tipo só recalculam ao tentar publicar de novo)
      // start/end/group nunca são considerados desconectados (containers visuais ou pontos terminais)
      if (node.data.type === 'start' || node.data.type === 'end' || node.data.type === 'group') return false;
      return !connectedIds.has(id);
    });

    if (stillInvalid.length !== invalidNodeIds.length) {
      setInvalidNodeIds(stillInvalid);
    }
  }, [nodes, edges, invalidNodeIds]);

  /**
   * Limpa manualmente o destaque de nodes inválidos (ex: usuário fecha o banner)
   */
  const clearInvalidNodes = useCallback(() => {
    setInvalidNodeIds([]);
  }, []);

  return {
    // Estado
    flowName,
    flowDescription,
    flowStatus,
    nodes,
    edges,
    variables,
    triggerConfig, // Configuração de gatilhos
    isLoading,
    isSaving,
    hasChanges,
    lastSavedAt,
    invalidNodeIds, // IDs de nodes com problema (destaque vermelho no canvas)
    
    // Ações principais
    saveFlow,
    publishFlow,
    pauseFlow,
    archiveFlow,
    loadFlow,
    saveTriggerConfig, // Salva gatilhos separadamente
    
    // Ações de nodes/edges
    updateNodes,
    updateEdges,
    updateNodeConfig,
    
    // Ações de variáveis
    addVariable,
    removeVariable,
    updateVariable,
    
    // Ações de metadados
    updateFlowName,
    updateFlowDescription,

    // Validação visual
    clearInvalidNodes, // Limpa destaque de nodes inválidos
  };
}
